// Tiny dependency-free Markdown renderer for the concierge (Aria) chat. Builds React elements — never
// innerHTML / dangerouslySetInnerHTML — so all text is rendered as text nodes and is XSS-safe by
// construction. Supports the small subset the assistant emits: paragraphs, single-line breaks, bullet
// and numbered lists, **bold**, *italic*, `inline code`, and [links](url) (http/https/mailto only).
// Anything it doesn't recognise falls through as plain text. Deliberately NOT a full CommonMark parser.
import type { ReactNode } from "react";

/** Only allow safe link schemes; otherwise the link renders as its literal markdown (no href). */
function safeHref(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u) || /^mailto:/i.test(u)) return u;
  return null;
}

const INLINE: Array<[RegExp, (m: RegExpMatchArray, key: string) => ReactNode]> = [
  [/^\*\*([^*]+)\*\*/, (m, k) => <strong key={k}>{m[1]}</strong>],
  [/^__([^_]+)__/, (m, k) => <strong key={k}>{m[1]}</strong>],
  [/^\*([^*\n]+)\*/, (m, k) => <em key={k}>{m[1]}</em>],
  [/^_([^_\n]+)_/, (m, k) => <em key={k}>{m[1]}</em>],
  [/^`([^`]+)`/, (m, k) => <code key={k}>{m[1]}</code>],
  [
    /^\[([^\]]+)\]\(([^)\s]+)\)/,
    (m, k) => {
      const href = safeHref(m[2]!);
      return href ? (
        <a key={k} href={href} target="_blank" rel="noopener noreferrer">
          {m[1]}
        </a>
      ) : (
        <span key={k}>{m[0]}</span>
      );
    },
  ],
];

/** Parse inline markdown within a single line into React nodes. Unmatched chars accumulate as text. */
function renderInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let rest = text;
  let buf = "";
  let i = 0;
  while (rest.length) {
    let matched = false;
    for (const [re, make] of INLINE) {
      const m = rest.match(re);
      if (m) {
        if (buf) {
          nodes.push(buf);
          buf = "";
        }
        nodes.push(make(m, `${keyBase}-${i++}`));
        rest = rest.slice(m[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      buf += rest[0];
      rest = rest.slice(1);
    }
  }
  if (buf) nodes.push(buf);
  return nodes;
}

/** Render a constrained subset of Markdown as safe React elements, wrapped in `.md`. */
export function Markdown({ text }: { text: string }): JSX.Element {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushPara = () => {
    if (!para.length) return;
    const key = `p${blocks.length}`;
    const inner: ReactNode[] = [];
    para.forEach((ln, idx) => {
      if (idx > 0) inner.push(<br key={`${key}-br${idx}`} />);
      inner.push(...renderInline(ln, `${key}-${idx}`));
    });
    blocks.push(<p key={key}>{inner}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const key = `l${blocks.length}`;
    const items = list.items.map((it, idx) => <li key={`${key}-${idx}`}>{renderInline(it, `${key}-${idx}`)}</li>);
    blocks.push(list.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>);
    list = null;
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (ul) {
      flushPara();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(ul[1]!);
    } else if (ol) {
      flushPara();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(ol[1]!);
    } else if (heading) {
      // Render headings as a bold paragraph — the chat bubble has no room for real heading hierarchy.
      flushPara();
      flushList();
      blocks.push(
        <p key={`h${blocks.length}`}>
          <strong>{renderInline(heading[1]!, `h${blocks.length}`)}</strong>
        </p>,
      );
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();

  return <div className="md">{blocks}</div>;
}
